// Channel day view (feature 006-channel-day-view). A per-channel day timeline that renders in
// place of FullCalendar's timeGridDay (as WeekPostsBoard does for timeGridWeek): a left time ruler,
// one column per managed channel, each post placed by time (overlaps split side-by-side), and a
// dashed current-time line across all channels. Reuses WeekPostCard (compact) + the --wb tokens.
import React, {useEffect, useRef} from "react";
import {format, isSameDay} from "date-fns";
import {OverlayTrigger, Popover} from "react-bootstrap";
import {PeerActivityType} from "../../redux/Content/PeerActivity/types";
import {ContentPeerChannelType} from "../../redux/Content/PeerChannel/types";
import {convertToMomentLocale, getHandlerIconByCode} from "../../helpers/functions";
import PopupItem from "../../blocks/Content/PeerActivity/Popup";
import WeekPostCard from "./WeekPostCard";
import VariantGroupCard from "./VariantGroupCard";
import {groupsForDay} from "./VariantGroupCard.helpers";
import {VariantGroupType} from "../../redux/Content/VariantGroup/types";
import {mapActivityToWeekCardModel} from "./WeekPostCard.helpers";
import {DayPostInput, layoutChannelPosts, minutesToPx, nowMinutes, resolveChannelColumns} from "./DayChannelBoard.helpers";

type Translate = (key: string, defaultValue?: string) => string;

interface Props {
    activities: PeerActivityType[];
    day: Date | null;
    channels: ContentPeerChannelType[];
    language: string;
    t: Translate;
    onPrev: () => void;
    onNext: () => void;
    onToday: () => void;
    onChangeView: (view: string) => void;
    onToggleIdeas: () => void;
    onEdit: (activity: PeerActivityType) => void;
    onDuplicate?: (activity: PeerActivityType) => void;
    onDelete?: (activity: PeerActivityType) => void;
    onRetry?: (activity: PeerActivityType) => void;
    onAddAt?: (channelUuid: string, day: Date, time: {h: number; m: number}) => void;
    // Native HTML5 drop of an ideas-sidebar card → create a post from that idea on the viewed day.
    // (Day-level: the idea opens in the compose modal with its own channel; the dropped-on channel
    // column is not pre-selected — boardIdeaDrop takes only a date, as month/week do.)
    onIdeaDrop?: (uuid: string, date: Date) => void;
    // Feature 012: planned slots with alternative versions, one card each, and how to open one.
    groups?: VariantGroupType[];
    onOpenGroup?: (group: VariantGroupType) => void;
}

const IDEA_MIME = 'application/x-m9s-idea';

const H = 58;           // hour height (px)
const COLW = 176;       // channel column width (px)
const RULER = 64;       // ruler width (px)

function activityDate(a: PeerActivityType): Date | null {
    const raw = a.activeFrom || a.start;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
}

const DayChannelBoard: React.FC<Props> = (props) => {
    const {activities, day, channels, language, t, onPrev, onNext, onToday, onChangeView, onToggleIdeas, onEdit, onDuplicate, onDelete, onRetry, onAddAt, onIdeaDrop, groups, onOpenGroup} = props;
    const locale = convertToMomentLocale(language);
    const now = new Date();
    const nowRef = useRef<HTMLDivElement>(null);
    // Whether a native idea-drag is over the columns area (affordance only).
    const [ideaOver, setIdeaOver] = React.useState<boolean>(false);

    const dayPosts = (activities || []).filter((a) => {
        const d = activityDate(a);
        return day != null && d != null && isSameDay(d, day);
    });
    // Columns = all managed channels, unioned with this DAY's post channels; count = this day's posts only.
    const cols = resolveChannelColumns(channels || [], dayPosts);

    const isToday = day != null && isSameDay(day, now);

    // Today: bring the current time into view via page scroll (no inner scroll box).
    useEffect(() => {
        if (isToday && nowRef.current) {
            nowRef.current.scrollIntoView({block: "center", inline: "nearest"});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day, isToday]);

    if (!day) return <div className="dayBoard dayBoard--loading"/>;

    const gridH = 24 * H;
    const title = format(day, "EEEE, d MMMM yyyy", {locale});

    const hourLabels = [];
    for (let h = 0; h <= 24; h++) {
        hourLabels.push(
            <div key={h} className="dayBoard__hour" style={{top: minutesToPx(h * 60, H)}}>
                {String(h).padStart(2, "0")}:00
            </div>,
        );
    }

    // posts per channel, placed + laned
    const postsByChannel: Record<string, PeerActivityType[]> = {};
    dayPosts.forEach((a) => {
        const uuid = (a.channel as any)?.uuid || "";
        if (!uuid) return;
        (postsByChannel[uuid] = postsByChannel[uuid] || []).push(a);
    });

    const nowLine = isToday && (
        <div className="dayBoard__now" ref={nowRef} style={{top: minutesToPx(nowMinutes(now), H)}} aria-hidden="true">
            <span className="dayBoard__nowLine"/>
        </div>
    );

    const handleSlotClick = (e: React.MouseEvent, channelUuid: string) => {
        if (!onAddAt) return;
        const colEl = e.currentTarget as HTMLElement;
        const y = e.clientY - colEl.getBoundingClientRect().top;
        const totalMin = Math.max(0, Math.min(24 * 60 - 15, Math.round((y / H) * 60 / 15) * 15));
        onAddAt(channelUuid, day, {h: Math.floor(totalMin / 60), m: totalMin % 60});
    };

    return (
        <div className="dayBoard">
            <div className="dayBoard__toolbar">
                <button type="button" className="dayBoard__icbtn dayBoard__icbtn--pink" title={t("menu:activitiesIdeas")} onClick={onToggleIdeas}>
                    <i className="mdi mdi-diamond-stone"/>
                </button>
                <div className="dayBoard__nav">
                    <button type="button" className="dayBoard__icbtn" aria-label="Previous day" onClick={onPrev}><i className="ri-arrow-left-s-line"/></button>
                    <div className="dayBoard__title">{title}</div>
                    <button type="button" className="dayBoard__icbtn" aria-label="Next day" onClick={onNext}><i className="ri-arrow-right-s-line"/></button>
                </div>
                <div className="dayBoard__seg">
                    <button type="button" className="dayBoard__segBtn" onClick={onToday}>{t("common:today", "Today")}</button>
                    <button type="button" className="dayBoard__segBtn dayBoard__segBtn--on" onClick={() => onChangeView("timeGridDay")}>{t("common:day", "Day")}</button>
                    <button type="button" className="dayBoard__segBtn" onClick={() => onChangeView("timeGridWeek")}>{t("common:week", "Week")}</button>
                    <button type="button" className="dayBoard__segBtn" onClick={() => onChangeView("dayGridMonth")}>{t("common:month", "Month")}</button>
                </div>
            </div>

            {/* Feature 012: a group spans channels, so its card sits above the channel columns. */}
            {day && groupsForDay(groups, day).length > 0 &&
                <div className="dayBoard__groups">
                    {groupsForDay(groups, day).map((group) => <VariantGroupCard key={group.uuid} group={group} t={t} onOpen={onOpenGroup}/>)}
                </div>}

            <div className="dayBoard__scroll">
                <div className="dayBoard__inner" style={{width: RULER + cols.length * COLW}}>
                    <div className="dayBoard__head">
                        <div className="dayBoard__corner" style={{width: RULER}}>
                            <span className="dayBoard__cornerDow">{format(day, "EEE", {locale})}</span>
                            <span className="dayBoard__cornerNum">{format(day, "dd", {locale})}</span>
                        </div>
                        {cols.map((c) => (
                            <div className="dayBoard__chHead" style={{width: COLW}} key={c.uuid}>
                                <div className="dayBoard__chPill">
                                    {c.platformCode
                                        ? <img className="dayBoard__chIc rounded-circle" src={getHandlerIconByCode(c.platformCode)} alt={c.platformName}/>
                                        : <span className="dayBoard__chIc dayBoard__chIc--fallback rounded-circle">{c.platformName ? c.platformName[0] : "?"}</span>}
                                    <span className="dayBoard__chName text-truncate" title={c.name}>{c.name}</span>
                                    {c.count > 0 && <span className="dayBoard__chCount">{c.count}</span>}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="dayBoard__gridRow">
                        <div className="dayBoard__ruler" style={{width: RULER, height: gridH}}>{hourLabels}</div>
                        <div
                            className={"dayBoard__cols" + (ideaOver ? " dayBoard__cols--ideaover" : "")}
                            style={{height: gridH}}
                            // Native idea-drop target (day level — the viewed day). Separate event
                            // system from the per-column onAddAt click, so neither interferes.
                            onDragOver={(e) => {
                                if (!onIdeaDrop) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'copy';
                            }}
                            onDragEnter={() => onIdeaDrop && setIdeaOver(true)}
                            onDragLeave={(e) => {
                                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                                setIdeaOver(false);
                            }}
                            onDrop={(e) => {
                                const uuid = e.dataTransfer.getData(IDEA_MIME);
                                setIdeaOver(false);
                                if (uuid && day) {
                                    e.preventDefault();
                                    onIdeaDrop?.(uuid, day);
                                }
                            }}
                        >
                            {cols.map((c) => {
                                const list = postsByChannel[c.uuid] || [];
                                const inputs: DayPostInput[] = list.map((a) => {
                                    const d = activityDate(a)!;
                                    const startMin = d.getHours() * 60 + d.getMinutes();
                                    const end = a.end ? new Date(a.end) : null;
                                    const durMin = end && !isNaN(end.getTime()) ? Math.max(15, Math.round((end.getTime() - d.getTime()) / 60000)) : 30;
                                    return {key: a.uuid || String(startMin), startMin, endMin: startMin + durMin};
                                });
                                const layouts = layoutChannelPosts(inputs, H);
                                const layoutByKey: Record<string, typeof layouts[number]> = {};
                                layouts.forEach((l) => (layoutByKey[l.key] = l));

                                return (
                                    <div className="dayBoard__col" style={{width: COLW, height: gridH}} key={c.uuid} onClick={(e) => handleSlotClick(e, c.uuid)}>
                                        {list.map((activity) => {
                                            const key = activity.uuid || "";
                                            const lay = layoutByKey[key];
                                            if (!lay) return null;
                                            const start = activityDate(activity);
                                            const model = {
                                                ...mapActivityToWeekCardModel(activity, start, activity.end ? new Date(activity.end) : null, t, language),
                                                isCollapsed: false,
                                            };
                                            const popover = (
                                                <Popover id={"dcb-" + model.uuid} style={{maxWidth: 400}}>
                                                    <PopupItem item={activity}/>
                                                </Popover>
                                            );
                                            const style: React.CSSProperties = {
                                                top: lay.topPx,
                                                left: `calc(${(lay.lane / lay.laneCount) * 100}% + 3px)`,
                                                width: `calc(${(1 / lay.laneCount) * 100}% - 6px)`,
                                            };
                                            return (
                                                <div
                                                    className="dayBoard__post"
                                                    style={style}
                                                    key={model.uuid}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onDoubleClick={(e) => {e.stopPropagation(); onEdit(activity);}}
                                                >
                                                    <OverlayTrigger trigger={["click"]} rootClose placement="auto" overlay={popover}>
                                                        <div>
                                                            <WeekPostCard
                                                                model={model}
                                                                onEdit={() => onEdit(activity)}
                                                                onDuplicate={onDuplicate ? () => onDuplicate(activity) : undefined}
                                                                onDelete={onDelete ? () => onDelete(activity) : undefined}
                                                                onRetry={onRetry ? () => onRetry(activity) : undefined}
                                                            />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                            {nowLine}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DayChannelBoard;
